import { request } from '@umijs/max';

export interface MenuListItem {
  id: number;
  name: string;
  code: string;
  icon?: string;
  path?: string;
  component?: string;
  isVirtual: boolean;
  functionCode?: string;
  openMethod: 'current' | 'new';
  displayOrder: number;
  parentId?: number;
  parentName?: string;
  status: string;
  children?: MenuListItem[];
  createTime?: string;
  updateTime?: string;
}

export interface MenuPageResult {
  data: MenuListItem[];
  total: number;
  current: number;
  pageSize: number;
  success: boolean;
}

/**
 * 获取菜单列表（分页）
 */
export async function getMenuList(params?: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  status?: string;
}) {
  return request<{
    data: MenuPageResult;
    success: boolean;
  }>('/api/system/menu/list', {
    method: 'GET',
    params,
  });
}

/**
 * 获取所有菜单列表（树形结构）
 */
export async function getMenusTree() {
  return request<{
    data: MenuListItem[];
    success: boolean;
  }>('/api/system/menu/tree', {
    method: 'GET',
  });
}

/**
 * 获取菜单详情
 */
export async function getMenuDetail(id: number) {
  return request<{
    data: MenuListItem;
    success: boolean;
  }>(`/api/system/menu/${id}`, {
    method: 'GET',
  });
}

/**
 * 创建菜单
 */
export async function createMenu(data: Omit<MenuListItem, 'id' | 'createTime' | 'updateTime' | 'children' | 'parentName'>) {
  return request<{
    data: MenuListItem;
    success: boolean;
  }>('/api/system/menu', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  });
}

/**
 * 更新菜单
 */
export async function updateMenu(id: number, data: Omit<MenuListItem, 'id' | 'createTime' | 'updateTime' | 'children' | 'parentName'>) {
  return request<{
    data: MenuListItem;
    success: boolean;
  }>(`/api/system/menu/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  });
}

/**
 * 删除菜单
 */
export async function deleteMenu(id: number) {
  return request<{
    success: boolean;
    message?: string;
  }>(`/api/system/menu/${id}`, {
    method: 'DELETE',
  });
}

/**
 * 批量删除菜单
 */
export async function batchDeleteMenu(ids: number[]) {
  return request<{
    success: boolean;
    message?: string;
  }>('/api/system/menu/batch', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    data: ids,
  });
}

/**
 * 根据角色ID获取菜单列表
 */
export async function getMenusByRoleId(roleId: number) {
  return request<{
    data: MenuListItem[];
    success: boolean;
  }>(`/api/system/menu/role/${roleId}`, {
    method: 'GET',
  });
}

/**
 * 根据用户ID获取菜单列表
 */
export async function getMenusByUserId(userId: number) {
  return request<{
    data: MenuListItem[];
    success: boolean;
  }>(`/api/system/menu/user/${userId}`, {
    method: 'GET',
  });
}

/**
 * 获取当前登录用户的菜单列表（基于角色权限）
 * 🔑 核心权限API：返回当前用户有权访问的菜单树
 */
export async function getCurrentUserMenus() {
  return request<{
    data: MenuListItem[];
    success: boolean;
    message?: string;
  }>('/api/user/menu/current', {
    method: 'GET',
  });
}

export interface MenuHomeRule {
  id?: number;
  matchMode: 'DEPARTMENT' | 'ROLE';
  deptId?: number;
  roleId?: number;
  priority: number;
  status: 'active' | 'inactive';
}

export async function getMenuHomeRules(menuId: number) {
  return request<API.Result<MenuHomeRule[]>>(`/api/system/menu/${menuId}/home-rules`, { method: 'GET' });
}

export async function saveMenuHomeRules(menuId: number, rules: MenuHomeRule[]) {
  return request<API.Result<MenuHomeRule[]>>(`/api/system/menu/${menuId}/home-rules`, {
    method: 'PUT',
    data: { rules },
  });
}
